import { ArrowDownIcon } from '@systemeio/ui/icons'
import { ActionMenu, ActionMenuItem, PrimaryButton } from '@systemeio/ui'
import React, { useState } from 'react'
import { ButtonWithDropdownTypeEnum } from 'shared/components/buttin-with-dropdown/enums/button-with-dropdown-type-enum'
import {
  ButtonWithDropdownOptionsType,
  ButtonWithDropdownStateType,
} from 'shared/components/buttin-with-dropdown/types/button-with-dropdown-interface'
import { ActionMenuLinkItem } from 'shared/components/table/components/action-menu-link-item'
import { twJoin, twMerge } from 'tailwind-merge'

export interface ButtonWithDropdown {
  options: ButtonWithDropdownOptionsType[]
  disabled: boolean
  onClick: () => Promise<void>
  title: string
  isFetching: boolean
}

export const ButtonWithDropdown = ({
  options,
  onClick,
  disabled,
  title,
  isFetching,
}: ButtonWithDropdown) => {
  const [isOpened, setIsOpened] = useState<ButtonWithDropdownStateType>(() => {
    const obj: ButtonWithDropdownStateType = {} as ButtonWithDropdownStateType
    options.forEach(value => {
      obj[value.name] = false
    })
    return obj
  })

  const actionSideElements: JSX.Element[] = []

  const itemRender = (el: ButtonWithDropdownOptionsType) => {
    const shouldRender = el.shouldRender !== undefined ? el.shouldRender : true

    if (shouldRender) {
      switch (el.type) {
        case ButtonWithDropdownTypeEnum.Button: {
          actionSideElements.push(
            el.onActionRender(
              isOpened[el.name],
              () => setIsOpened(prev => ({ ...prev, [el.name]: false })),
              `${el.name}-modal`,
            ),
          )
          return (
            <ActionMenuItem
              key={`${el.name}-custom`}
              disabled={el.menuItem?.disabled}
              className={twMerge(
                'px-3 py-2.5 text-[15px] font-medium lg:px-4 lg:py-3.5',
                !el.menuItem?.disabled && 'text-text-primary',
              )}
              onClick={async () => {
                setIsOpened(prev => ({ ...prev, [el.name]: true }))
              }}
              label={
                <div className={`flex gap-2`}>
                  {el.menuItem?.icon}
                  <span className={'truncate'}>{el.menuItem.title}</span>
                </div>
              }
            ></ActionMenuItem>
          )
        }
        case ButtonWithDropdownTypeEnum.Link:
          return (
            <ActionMenuLinkItem
              isNext={el.isNext}
              openInNew={el.openInNew}
              key={`${el.name}-open-link`}
              href={el.setHref()}
              disabled={el.menuItem?.disabled}
              className={twMerge(
                'px-3 py-2.5 text-[15px] font-medium lg:px-4 lg:py-3.5',
                !el.menuItem?.disabled && 'text-text-primary',
              )}
              label={
                <>
                  {el.menuItem?.icon}
                  <span className={'truncate'}>{el.menuItem.title}</span>
                </>
              }
            />
          )
      }
    }
  }

  const items = options.map(el => itemRender(el))

  const hasItems = items.filter(Boolean).length !== 0

  return (
    <div
      className={`bg-brand-primary flex rounded-lg ${twJoin(
        disabled && 'bg-text-secondary/50 pointer-events-none',
      )}`}
    >
      <PrimaryButton
        className={twJoin(hasItems && 'rounded-r-none')}
        isFetching={isFetching}
        disabled={disabled}
        onClick={() => onClick()}
      >
        {title}
      </PrimaryButton>
      {hasItems && (
        <ActionMenu menuItems={items}>
          {({ active }) => (
            <div
              className={
                'main-transition-colors hover:bg-brand-primary-hover border-l-background-secondary rounded-r-lg  border-l p-4 '
              }
            >
              <ArrowDownIcon
                className={twMerge(
                  'stroke-background-secondary transition-transform duration-300',
                  active && 'rotate-180',
                )}
                aria-hidden="true"
              />
            </div>
          )}
        </ActionMenu>
      )}
      {actionSideElements.map(el => (
        <div key={el.key}>{el}</div>
      ))}
    </div>
  )
}
